import { z } from "zod";
import {
  EXPERIMENT_ENTITY_PREFIX,
  isValidPrefixedKsuid,
  PROJECT_ENTITY_PREFIX,
} from "@/utils/ksuidGenerator";
import { DATA_TRACKER_COLUMN_TYPES } from "@/models/cns";
import { parseCatalogEntityPayload } from "@/lib/toolCardSchemas/parseCatalogEntityPayload";

const prefixedKsuid = (prefix: string, label: string) =>
  z.string().refine((id) => isValidPrefixedKsuid(id, prefix), {
    message: `Invalid ${label} public ID`,
  });

const TrackerColumnSchema = z.object({
  id: z.string().min(1).max(64),
  header: z.string().min(1),
  type: z.enum(DATA_TRACKER_COLUMN_TYPES),
  options: z.array(z.string().min(1)).optional(),
  // Suggestions follow the nullable REST wire contract. The Action Request submission
  // contract rejects null, so canonicalize it to an omitted optional property.
  description: z
    .string()
    .nullish()
    .transform((description) => description ?? undefined),
});

export const SetupTrackerPayloadSchema = z.object({
  experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
  experimentTitle: z.string().min(1).optional(),
  projectPublicId: prefixedKsuid(PROJECT_ENTITY_PREFIX, "project"),
  rowUnit: z.string().min(1).optional(),
  rationale: z.string().min(1).optional(),
});

export const SetupTrackerResolveSchema = z.object({
  columns: z.array(TrackerColumnSchema).min(1),
});

export type SetupTrackerPayload = z.infer<typeof SetupTrackerPayloadSchema>;
export type SetupTrackerResolve = z.infer<typeof SetupTrackerResolveSchema>;

export type SetupTrackerToolResult = SetupTrackerResolve & {
  toolCallId: string;
};

export type SetupTrackerArgs = {
  entities?: Array<{
    type?: string;
    experimentPublicId?: string;
    experimentTitle?: string;
    projectPublicId?: string;
    rowUnit?: string;
    rationale?: string;
    toolInvoke?: { toolCallId?: string };
  }>;
  experimentPublicId?: string;
  experimentTitle?: string;
  projectPublicId?: string;
  rowUnit?: string;
  rationale?: string;
};

export function parseSetupTrackerPayload(args: unknown, toolCallId?: string) {
  return parseCatalogEntityPayload(
    SetupTrackerPayloadSchema,
    "setup_tracker",
    args,
    toolCallId,
    (entity) => ({
      experimentPublicId: entity.experimentPublicId,
      experimentTitle: entity.experimentTitle,
      projectPublicId: entity.projectPublicId,
      rowUnit: entity.rowUnit,
      rationale: entity.rationale,
    }),
  );
}
